import axios from "axios";

// Signature of poll payloads that ignores per-tick stamps (feedTs/ts/updatedAt) so React
// only re-renders when something the user can see actually changed.
const VOLATILE = new Set(["feedTs", "updatedAt", "ts", "oddsHold"]);
export const stableSig = (data) => JSON.stringify(data, (k, v) => (VOLATILE.has(k) ? undefined : v));

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: BASE });

// Attach user token by default
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cb_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authApi = {
  register: (payload) => api.post("/auth/register", payload).then((r) => r.data),
  login: (payload) => api.post("/auth/login", payload).then((r) => r.data),
  resetPassword: (payload) => api.post("/auth/reset-password", payload).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const matchesApi = {
  list: (status) => api.get("/matches", { params: status ? { status } : {} }).then((r) => r.data),
  get: (id) => api.get(`/matches/${id}`).then((r) => r.data),
};

export const betsApi = {
  list: () => api.get("/bets").then((r) => r.data),
  place: (payload) => api.post("/bets", payload).then((r) => r.data),
  cashout: (betId) => api.post(`/bets/${betId}/cashout`).then((r) => r.data),
};

export const sessionBetsApi = {
  list: () => api.get("/session-bets").then((r) => r.data),
  place: (payload) => api.post("/session-bets", payload).then((r) => r.data),
};

export const tossBetsApi = {
  list: () => api.get("/toss-bets").then((r) => r.data),
  place: (payload) => api.post("/toss-bets", payload).then((r) => r.data),
};

export const ezugiApi = {
  games: () => api.get("/ezugi/games").then((r) => r.data),
  sportsbook: () => api.get("/ezugi/sportsbook").then((r) => r.data),
  launch: (gameId, returnPath = "") => api.post("/ezugi/launch", { gameId, returnPath }, { timeout: 45000 }).then((r) => r.data),
};

export const casinoApi = {
  catalogue: () => api.get("/casino/catalogue").then((r) => r.data),  // slots + live + hub in one edge-cacheable call
};

export const megaApi = {
  games: (category) => api.get("/meganodes/games", { params: { category } }).then((r) => r.data),
  launch: (provider_code, game_symbol) => api.post("/meganodes/launch", { provider_code, game_symbol }, { timeout: 45000 }).then((r) => r.data),
  history: (category) => api.get("/meganodes/history", { params: { category } }).then((r) => r.data),
  adminStatus: (token) => api.get("/meganodes/admin/status", adminHeaders(token)).then((r) => r.data),
};

export const walletApi = {
  get: () => api.get("/wallet").then((r) => r.data),
  gameHistory: () => api.get("/wallet/game-history").then((r) => r.data),
  withdraw: (payload) => api.post("/wallet/withdraw", payload).then((r) => r.data),
  bankDetails: () => api.get("/wallet/bank-details").then((r) => r.data),
  manualDeposit: (payload) => api.post("/wallet/manual-deposit", payload).then((r) => r.data),
  manualDepositList: () => api.get("/wallet/manual-deposits").then((r) => r.data),
  bankAccounts: () => api.get("/wallet/bank-accounts").then((r) => r.data),
  deleteBankAccount: (id) => api.delete(`/wallet/bank-accounts/${id}`).then((r) => r.data),
  upiCreate: (amount) => api.post("/wallet/upi/create", { amount }).then((r) => r.data),
  upiStatus: (clientTxnId) => api.get(`/wallet/upi/status/${encodeURIComponent(clientTxnId)}`).then((r) => r.data),
  upiDeposits: () => api.get("/wallet/upi/deposits").then((r) => r.data),
};

export const referralApi = {
  get: () => api.get("/referrals").then((r) => r.data),
};

export const affiliateApi = {
  get: () => api.get("/affiliate").then((r) => r.data),
  create: () => api.post("/affiliate/create", {}).then((r) => r.data),
};

export const gamesApi = {
  roulette: (payload) => api.post("/games/roulette/play", payload).then((r) => r.data),
  miniRoulette: (payload) => api.post("/games/mini-roulette/play", payload).then((r) => r.data),
  proRoulette: (payload) => api.post("/games/pro-roulette/play", payload).then((r) => r.data),
  dice: (payload) => api.post("/games/dice/play", payload).then((r) => r.data),
  minesStart: (payload) => api.post("/games/mines/start", payload).then((r) => r.data),
  minesReveal: (payload) => api.post("/games/mines/reveal", payload).then((r) => r.data),
  minesCashout: (payload) => api.post("/games/mines/cashout", payload).then((r) => r.data),
  hiloStart: (payload) => api.post("/games/hilo/start", payload).then((r) => r.data),
  hiloGuess: (payload) => api.post("/games/hilo/guess", payload).then((r) => r.data),
  hiloCashout: (payload) => api.post("/games/hilo/cashout", payload).then((r) => r.data),
  plinkoDrop: (payload) => api.post("/games/plinko/drop", payload).then((r) => r.data),
  towerStart: (payload) => api.post("/games/tower/start", payload).then((r) => r.data),
  towerPick: (payload) => api.post("/games/tower/pick", payload).then((r) => r.data),
  towerCashout: (payload) => api.post("/games/tower/cashout", payload).then((r) => r.data),
  limboPlay: (payload) => api.post("/games/limbo/play", payload).then((r) => r.data),
  coinflipStart: (payload) => api.post("/games/coinflip/start", payload).then((r) => r.data),
  coinflipFlip: (payload) => api.post("/games/coinflip/flip", payload).then((r) => r.data),
  coinflipCashout: (payload) => api.post("/games/coinflip/cashout", payload).then((r) => r.data),
};

export const chatApi = {
  list: () => api.get("/chat/messages").then((r) => r.data),
  send: (text) => api.post("/chat/messages", { text }).then((r) => r.data),
  unread: () => api.get("/chat/unread").then((r) => r.data),
};

export const supportApi = {
  createTicket: (payload) => api.post("/support/tickets", payload).then((r) => r.data),
};

const adminHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const adminApi = {
  login: (payload) => api.post("/admin/login", payload).then((r) => r.data),
  stats: (token) => api.get("/admin/stats", adminHeaders(token)).then((r) => r.data),
  resetStats: (token) => api.post("/admin/stats/reset", {}, adminHeaders(token)).then((r) => r.data),
  users: (token) => api.get("/admin/users", adminHeaders(token)).then((r) => r.data),
  userBets: (token, userId) => api.get(`/admin/users/${userId}/bets`, adminHeaders(token)).then((r) => r.data),
  adjustChips: (token, userId, amount, note) =>
    api.post(`/admin/users/${userId}/adjust`, { amount, note }, adminHeaders(token)).then((r) => r.data),
  blockUser: (token, userId, blocked) =>
    api.post(`/admin/users/${userId}/block`, { blocked }, adminHeaders(token)).then((r) => r.data),
  deleteUser: (token, userId) =>
    api.delete(`/admin/users/${userId}`, adminHeaders(token)).then((r) => r.data),
  chatThreads: (token) => api.get("/admin/chat/threads", adminHeaders(token)).then((r) => r.data),
  chatThread: (token, userId) => api.get(`/admin/chat/${userId}`, adminHeaders(token)).then((r) => r.data),
  chatReply: (token, userId, text) =>
    api.post(`/admin/chat/${userId}`, { text }, adminHeaders(token)).then((r) => r.data),
  bets: (token) => api.get("/admin/bets", adminHeaders(token)).then((r) => r.data),
  betsByKind: (token, kind) => api.get(`/admin/bets?kind=${kind}`, adminHeaders(token)).then((r) => r.data),
  cancelSessionBet: (token, betId) =>
    api.post(`/admin/session-bets/${betId}/cancel`, {}, adminHeaders(token)).then((r) => r.data),
  matches: () => api.get("/matches", { params: { fresh: 1 } }).then((r) => r.data),
  setStatus: (token, matchId, status) =>
    api.post(`/admin/matches/${matchId}/status`, { status }, adminHeaders(token)).then((r) => r.data),
  setSuspend: (token, matchId, suspended) =>
    api.post(`/admin/matches/${matchId}/suspend`, { suspended }, adminHeaders(token)).then((r) => r.data),
  setSessionSuspend: (token, matchId, suspended) =>
    api.post(`/admin/matches/${matchId}/session-suspend`, { suspended }, adminHeaders(token)).then((r) => r.data),
  deleteMatch: (token, matchId) =>
    api.delete(`/admin/matches/${matchId}`, adminHeaders(token)).then((r) => r.data),
  cancelBet: (token, betId) =>
    api.post(`/admin/bets/${betId}/cancel`, {}, adminHeaders(token)).then((r) => r.data),
  tossPending: (token) => api.get("/admin/toss/pending", adminHeaders(token)).then((r) => r.data),
  settleToss: (token, matchId, winner) => api.post(`/admin/matches/${matchId}/settle-toss`, { winner }, adminHeaders(token)).then((r) => r.data),
  settle: (token, matchId, marketName, winningSelectionLabel) =>
    api.post(`/admin/matches/${matchId}/settle`, { marketName, winningSelectionLabel }, adminHeaders(token)).then((r) => r.data),
  updateOdds: (token, matchId, markets) =>
    api.post(`/admin/matches/${matchId}/odds`, { markets }, adminHeaders(token)).then((r) => r.data),
  getAutoOdds: (token) => api.get("/admin/settings/auto-odds", adminHeaders(token)).then((r) => r.data),
  setAutoOdds: (token, enabled) =>
    api.post("/admin/settings/auto-odds", { enabled }, adminHeaders(token)).then((r) => r.data),
  withdrawals: (token) => api.get("/admin/withdrawals", adminHeaders(token)).then((r) => r.data),
  withdrawalAction: (token, id, action) =>
    api.post(`/admin/withdrawals/${id}/action`, { status: action }, adminHeaders(token)).then((r) => r.data),
  manualDeposits: (token) => api.get("/admin/manual-deposits", adminHeaders(token)).then((r) => r.data),
  upiDeposits: (token) => api.get("/wallet/upi/admin/deposits", adminHeaders(token)).then((r) => r.data),
  upiRecheck: (token, id) => api.post(`/wallet/upi/admin/deposits/${id}/recheck`, {}, adminHeaders(token)).then((r) => r.data),
  payoutConfig: (token) => api.get("/wallet/upi/admin/payout/config", adminHeaders(token)).then((r) => r.data),
  payout: (token, id) => api.post(`/wallet/upi/admin/withdrawals/${id}/payout`, {}, adminHeaders(token)).then((r) => r.data),
  payoutRecheck: (token, id) => api.post(`/wallet/upi/admin/withdrawals/${id}/payout/recheck`, {}, adminHeaders(token)).then((r) => r.data),
  manualDepositAction: (token, id, action) =>
    api.post(`/admin/manual-deposits/${id}/action`, { status: action }, adminHeaders(token)).then((r) => r.data),
  auditLogs: (token, event) =>
    api.get("/admin/audit-logs", { ...adminHeaders(token), params: event ? { event } : {} }).then((r) => r.data),
  getBankDetails: (token) => api.get("/admin/bank-details", adminHeaders(token)).then((r) => r.data),
  setBankDetails: (token, payload) => api.post("/admin/bank-details", payload, adminHeaders(token)).then((r) => r.data),
  getManualPayMethods: (token) => api.get("/admin/manual-pay-methods", adminHeaders(token)).then((r) => r.data),
  setManualPayMethods: (token, payload) => api.post("/admin/manual-pay-methods", payload, adminHeaders(token)).then((r) => r.data),
  tickets: (token) => api.get("/admin/tickets", adminHeaders(token)).then((r) => r.data),
  resolveTicket: (token, id) => api.post(`/admin/tickets/${id}/resolve`, {}, adminHeaders(token)).then((r) => r.data),
  commentTicket: (token, id, text) => api.post(`/admin/tickets/${id}/comment`, { text }, adminHeaders(token)).then((r) => r.data),
  chatUserTickets: (token, userId) => api.get(`/admin/chat/${userId}/tickets`, adminHeaders(token)).then((r) => r.data),
  chatClose: (token, userId) => api.delete(`/admin/chat/${userId}`, adminHeaders(token)).then((r) => r.data),
  houseReport: (token) => api.get("/admin/house-report", adminHeaders(token)).then((r) => r.data),
  resetHouse: (token) => api.post("/admin/house-report/reset", {}, adminHeaders(token)).then((r) => r.data),
  affiliates: (token) => api.get("/admin/affiliates", adminHeaders(token)).then((r) => r.data),
  payAffiliate: (token, id) => api.post(`/admin/affiliates/${id}/pay`, {}, adminHeaders(token)).then((r) => r.data),
  setVip: (token, id, vip) => api.post(`/admin/users/${id}/vip`, { vip }, adminHeaders(token)).then((r) => r.data),
};

export default api;
